import { useQuery } from "@tanstack/react-query";
import { DEFAULT_OVERHEAD_PCT, type OrderDto } from "@workcost/shared";
import { fetchConfig, fetchPlans, listOrders } from "./billingApi";
import { isMockApi } from "./estimateApi";
import { useApiReady } from "./jobsQueries";
import { sortPlans } from "./plans";

export const plansKey = ["plans"] as const;
export const ordersKey = ["orders"] as const;
export const configKey = ["config"] as const;

/** แพ็กเกจจาก GET /plans (สาธารณะ ไม่ต้องเข้าสู่ระบบ) */
export function usePlans() {
  return useQuery({ queryKey: plansKey, queryFn: async () => sortPlans(await fetchPlans()), staleTime: 60_000 });
}

/** ค่าที่ client ต้องรู้จาก GET /config ถ้าโหลดไม่ได้ใช้ค่าสำรองจาก shared (ไม่ retry เพื่อไม่ให้หน้าผลประเมินรอนาน) */
export function useAppConfig() {
  return useQuery({ queryKey: configKey, queryFn: fetchConfig, staleTime: 10 * 60_000, retry: false });
}

/** ค่าดำเนินการตั้งต้น (%) ที่ใช้เริ่มสถานะแก้ค่า */
export function overheadFrom(cfg: { default_overhead_pct: number } | undefined): number {
  const v = cfg?.default_overhead_pct;
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : DEFAULT_OVERHEAD_PCT;
}

/** คำสั่งซื้อที่ยังไม่จบ: รอชำระ (ยังไม่หมดเวลา) หรือรอเจ้าหน้าที่ตรวจสอบ ใช้แสดงเป็นรายการ "กำลังดำเนินการ" ให้ลูกค้าตามต่อได้จากทุกหน้า */
export function isInProgress(o: Pick<OrderDto, "status" | "expiresAt">, now = Date.now()): boolean {
  if (o.status === "pending_review") return true;
  return (o.status === "awaiting_payment" || o.status === "pending") && Date.parse(o.expiresAt) > now;
}

/** ประวัติคำสั่งซื้อ GET /orders (ต้องเข้าสู่ระบบ) ถามซ้ำทุก 20 วินาทีขณะมีรายการรอตรวจสอบ เพื่อให้เห็นผลอนุมัติ/ปฏิเสธโดยไม่ต้องรีเฟรช */
export function useOrders() {
  const { ready } = useApiReady();
  return useQuery({
    queryKey: ordersKey,
    queryFn: listOrders,
    enabled: ready || isMockApi(),
    staleTime: 30_000,
    refetchInterval: (q) => ((q.state.data as OrderDto[] | undefined)?.some((o) => o.status === "pending_review") ? 20_000 : false),
  });
}

/** รายการที่กำลังดำเนินการของผู้ใช้ (ว่างเมื่อยังไม่เข้าสู่ระบบหรือยังโหลดไม่เสร็จ) */
export function useInProgressOrders(): OrderDto[] {
  const orders = useOrders();
  return (orders.data ?? []).filter((o) => isInProgress(o));
}
